import type { SurveyProgress, SurveyQuestion } from '@chiroup/core/types/Survey.type';

import { Icon } from '@chiroup/components/Icon';
import { TolerantTouchButton } from '@chiroup/components/TolerantTouchButton';
import { Icons } from '@chiroup/components/icons';
import React from 'react';
import { renderSurveyHtml } from './htmlRenderer';
import { surveyVerbiage } from './types';

// Soft Card primitives — small, single-purpose components that the
// per-type question renderers compose. Designing for swap-ability:
// to try a new visual variation, copy the primitives you want to
// change, modify them, and pass the alternative renderers into
// `<SoftSurveyQuestion renderers={...} />` (or compose them into
// a fork of the question types directly).

// -- Pill submit button ----------------------------------------------------

export const SoftPillButton: React.FC<{
  primaryColor: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  testCy?: string;
  type?: 'button' | 'submit';
}> = ({ primaryColor, disabled, onClick, children, testCy, type = 'button' }) => (
  <TolerantTouchButton
    type={type}
    onPress={onClick}
    disabled={disabled}
    data-cy={testCy}
    className="self-start inline-flex items-center justify-center rounded-full px-7 py-3 text-sm font-semibold text-white disabled:opacity-50 active:scale-[0.98] transition-transform"
    style={{ backgroundColor: primaryColor }}
  >
    {children}
  </TolerantTouchButton>
);

// -- Top progress pill -----------------------------------------------------

export const SoftProgressBar: React.FC<{
  progress: SurveyProgress | undefined;
  primaryColor: string;
}> = ({ progress, primaryColor }) => {
  const pct =
    progress && progress.total ? Math.round((progress.completed / progress.total) * 100) : 0;

  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-1.5 rounded-full bg-gray-950/5 dark:bg-white/10 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${pct}%`, backgroundColor: primaryColor }}
          aria-hidden="true"
        />
      </div>
      {progress && !progress.hideCount && (
        <p className="text-xs font-semibold tabular-nums text-gray-500 dark:text-gray-400 shrink-0">
          {progress.completed} / {progress.total}
        </p>
      )}
    </div>
  );
};

// -- Question heading (HTML-aware) -----------------------------------------

export const SoftQuestionTitle: React.FC<{ html: string; className?: string }> = ({
  html,
  className,
}) => (
  <h2
    className={
      className ??
      'text-xl sm:text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100 text-balance'
    }
  >
    {renderSurveyHtml(html)}
  </h2>
);

// -- Brand-tinted card around the question heading -------------------------

export const SoftQuestionCard: React.FC<{
  primaryColor: string;
  children: React.ReactNode;
}> = ({ primaryColor, children }) => (
  <div
    className="rounded-3xl p-6 sm:p-7 dark:ring-1 dark:ring-white/10"
    style={{ backgroundColor: `${primaryColor}12` }}
  >
    {children}
  </div>
);

// -- "← Previous" link -----------------------------------------------------

export const SoftPreviousLink: React.FC<{
  language: 'en' | 'es';
  onBack: () => void;
}> = ({ language, onBack }) => (
  <TolerantTouchButton
    onPress={onBack}
    className="self-start text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
  >
    {surveyVerbiage[language].previous}
  </TolerantTouchButton>
);

// -- Outer shell composing the above --------------------------------------

export const SoftQuestionShell: React.FC<{
  question: SurveyQuestion;
  progress: SurveyProgress | undefined;
  language: 'en' | 'es';
  primaryColor: string;
  hidden: boolean;
  showPrevious: boolean;
  onBack: () => void;
  children: React.ReactNode;
}> = ({ question, progress, language, primaryColor, hidden, showPrevious, onBack, children }) => (
  <div
    // pointer-events-none: while faded out the question is still mounted, so
    // without it a stray tap/click (e.g. the second half of a double-click on
    // "Get started") answers the not-yet-visible first option.
    className={`flex flex-col gap-5 transition-opacity ${
      hidden ? 'opacity-0 pointer-events-none' : 'opacity-100'
    }`}
  >
    <SoftProgressBar progress={progress} primaryColor={primaryColor} />
    <SoftQuestionCard primaryColor={primaryColor}>
      <SoftQuestionTitle html={question.question} />
    </SoftQuestionCard>
    {children}
    {showPrevious && <SoftPreviousLink language={language} onBack={onBack} />}
  </div>
);

// -- Tappable Soft Card row used by radio + checkbox ----------------------

export const SoftOptionCard: React.FC<{
  primaryColor: string;
  disabled?: boolean;
  onClick: () => void;
  alignTop?: boolean;
  testCy?: string;
  children: React.ReactNode;
}> = ({ primaryColor, disabled, onClick, alignTop, testCy, children }) => (
  <TolerantTouchButton
    onPress={onClick}
    disabled={disabled}
    data-cy={testCy}
    className={`group w-full ${
      alignTop ? 'items-start' : 'items-center'
    } flex gap-3 text-left rounded-2xl px-4 py-3.5 portal-card hover:ring-2 hover:ring-[--ring-color] focus-visible:ring-2 focus-visible:ring-[--ring-color] focus-visible:outline-none transition-shadow disabled:opacity-50`}
    style={{ ['--ring-color' as any]: primaryColor } as React.CSSProperties}
  >
    {children}
  </TolerantTouchButton>
);

// -- Round radio dot (filled when selected) -------------------------------

export const SoftRadioMarker: React.FC<{
  primaryColor: string;
  selected?: boolean;
}> = ({ primaryColor, selected }) => (
  <span
    className={`size-5 rounded-full border-2 shrink-0 transition-colors ${
      selected ? '' : 'group-hover:bg-[--bg-color] group-focus-visible:bg-[--bg-color]'
    }`}
    style={
      {
        borderColor: primaryColor,
        backgroundColor: selected ? primaryColor : undefined,
        ['--bg-color' as any]: primaryColor,
      } as React.CSSProperties
    }
    aria-hidden="true"
  />
);

// -- Square checkbox marker (filled with check icon when selected) --------

export const SoftCheckboxMarker: React.FC<{
  primaryColor: string;
  checked?: boolean;
}> = ({ primaryColor, checked }) => (
  <span
    className="size-5 rounded-md border-2 shrink-0 mt-0.5 flex items-center justify-center text-white"
    style={{
      borderColor: primaryColor,
      backgroundColor: checked ? primaryColor : 'transparent',
    }}
    aria-hidden="true"
  >
    {checked && <Icon icon={Icons.checkCircle} className="size-3.5" />}
  </span>
);

// -- Styled text input + textarea (shared class) --------------------------

export const inputClass =
  'w-full rounded-xl border border-gray-300 dark:border-white/15 bg-white dark:bg-white/5 px-3 py-2.5 text-base text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:border-transparent disabled:opacity-50';

export const ringStyle = (primaryColor: string): React.CSSProperties => ({
  ['--tw-ring-color' as any]: primaryColor,
});

export const SoftTextField = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { primaryColor: string }
>(({ primaryColor, className, style, ...rest }, ref) => (
  <input
    ref={ref}
    type={rest.type ?? 'text'}
    {...rest}
    className={`${inputClass} ${className ?? ''}`}
    style={{ ...ringStyle(primaryColor), ...style }}
  />
));
SoftTextField.displayName = 'SoftTextField';

export const SoftTextArea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { primaryColor: string }
>(({ primaryColor, className, style, ...rest }, ref) => (
  <textarea
    ref={ref}
    {...rest}
    className={`${inputClass} resize-y min-h-28 ${className ?? ''}`}
    style={{ ...ringStyle(primaryColor), ...style }}
  />
));
SoftTextArea.displayName = 'SoftTextArea';
